import BrandIcon from '@/components/BrandIcon';
import { Users, Mail, Layout, FileSignature } from 'lucide-react';
import {
  SiStripe,
  SiCalendly,
  SiWordpress,
  SiMailchimp,
  SiGooglemeet,
  SiNotion,
  SiJira,
  SiIntercom,
  SiPaypal,
} from 'react-icons/si';

export interface Integration {
  id: string;
  name: string;
  category: string;
  Icon: any;
  iconType: 'brand' | 'si' | 'lucide';
  color: string;
  howItWorks: string;
  benefit: string;
  scalability: string;
  handling: string;
}

export const integrations: Integration[] = [
  // Communication
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    category: 'Communication',
    Icon: BrandIcon,
    iconType: 'brand',
    color: '#25D366',
    howItWorks: 'Connect your WhatsApp Business number to Zuri Hub. Every incoming message becomes a conversation in the unified inbox.',
    benefit: 'Support customers on their favorite app without switching screens. Rich media, quick replies, and templates included.',
    scalability: 'Handles high message volumes across multiple agents with shared context and auto-assignment.',
    handling: 'Inbound and outbound messages are logged, encrypted, and tied to customer profiles for full history.',
  },
  {
    id: 'telegram',
    name: 'Telegram',
    category: 'Communication',
    Icon: BrandIcon,
    iconType: 'brand',
    color: '#0088cc',
    howItWorks: 'Link your Telegram bot to Zuri Hub. Chats, groups, and direct messages flow into one workspace.',
    benefit: 'Reach privacy-conscious users and technical communities on a platform they trust.',
    scalability: 'Bot-based routing lets one workspace serve thousands of Telegram users with minimal overhead.',
    handling: 'Messages are delivered through Telegram APIs with built-in rate limit handling and retry logic.',
  },
  {
    id: 'instagram',
    name: 'Instagram',
    category: 'Communication',
    Icon: BrandIcon,
    iconType: 'brand',
    color: '#E1306C',
    howItWorks: 'Connect Instagram Direct and comments to Zuri Hub. DM notifications become support tickets.',
    benefit: 'Convert social engagement into real conversations and capture leads before they scroll away.',
    scalability: 'Supports multiple Instagram accounts and shared inboxes for large social teams.',
    handling: 'All DMs and comment replies are tracked, routed, and stored under the customer record.',
  },
  {
    id: 'messenger',
    name: 'Messenger',
    category: 'Communication',
    Icon: BrandIcon,
    iconType: 'brand',
    color: '#0084FF',
    howItWorks: 'Hook up your Facebook page to Zuri Hub so Messenger conversations appear alongside other channels.',
    benefit: 'Answer Facebook DMs faster with AI suggestions and shared team context.',
    scalability: 'Automated handoffs let a small team manage a growing Messenger audience.',
    handling: 'Facebook webhooks are processed, signed, and stored with conversation threading intact.',
  },
  {
    id: 'google-meet',
    name: 'Google Meet',
    category: 'Communication',
    Icon: SiGooglemeet,
    iconType: 'si',
    color: '#00897B',
    howItWorks: 'Generate a Google Meet link from any conversation or ticket with one click.',
    benefit: 'Move from chat to face-to-face instantly when issues need a personal touch.',
    scalability: 'Each link is unique and tied to the conversation record for audit and follow-up.',
    handling: 'Links are created via the Google Calendar API and shared back into the customer thread.',
  },
  {
    id: 'microsoft-teams',
    name: 'Microsoft Teams',
    category: 'Communication',
    Icon: Users,
    iconType: 'lucide',
    color: '#6264A7',
    howItWorks: 'Surface Microsoft Teams messages in Zuri Hub or push customer alerts to the right channel.',
    benefit: 'Keep internal and customer conversations in sync without leaving Teams.',
    scalability: 'Channel-level and direct-message routing adapts to any team size.',
    handling: 'Teams messages are synchronized bidirectionally with clear sender attribution.',
  },

  // Productivity & Ops
  {
    id: 'notion',
    name: 'Notion',
    category: 'Productivity & Ops',
    Icon: SiNotion,
    iconType: 'si',
    color: '#000000',
    howItWorks: 'Create Notion pages from tickets, conversations, and notes with one click.',
    benefit: 'Turn support insights into living documentation your whole company can use.',
    scalability: 'Bulk export and templated pages make it easy to build a knowledge base at scale.',
    handling: 'Data is synced to your chosen Notion workspace using the official integration token.',
  },
  {
    id: 'jira',
    name: 'Jira Software',
    category: 'Productivity & Ops',
    Icon: SiJira,
    iconType: 'si',
    color: '#0052CC',
    howItWorks: 'Escalate support issues to Jira tickets directly from a conversation.',
    benefit: 'Bridge support and engineering with shared context, status updates, and comments.',
    scalability: 'Map custom fields and issue types to support any Jira workflow.',
    handling: 'Ticket creation, comments, and status changes are synced both ways.',
  },
  {
    id: 'monday',
    name: 'Monday',
    category: 'Productivity & Ops',
    Icon: Layout,
    iconType: 'lucide',
    color: '#F02D65',
    howItWorks: 'Create Monday items from leads and tickets, then track progress in Zuri Hub.',
    benefit: 'Connect customer workflows to project management without copy-pasting.',
    scalability: 'Board and column mapping supports complex team structures and automations.',
    handling: 'Updates are pushed through the Monday GraphQL API and reflected in real time.',
  },
  {
    id: 'zurichat',
    name: 'ZuriChat',
    category: 'Productivity & Ops',
    Icon: BrandIcon,
    iconType: 'brand',
    color: '#2563eb',
    howItWorks: 'ZuriChat is the built-in channel for your workspace. Customers and agents chat in real time.',
    benefit: 'A native experience with full control over branding, data, and routing.',
    scalability: 'Runs on the same global edge as Zuri Hub, so it scales with your traffic.',
    handling: 'Conversations are encrypted, stored, and tied to customer history automatically.',
  },

  // Marketing & CRM
  {
    id: 'mailchimp',
    name: 'Mailchimp',
    category: 'Marketing & CRM',
    Icon: SiMailchimp,
    iconType: 'si',
    color: '#FFE01B',
    howItWorks: 'Sync contacts and conversations to Mailchimp audiences and email campaigns.',
    benefit: 'Turn support interactions into targeted follow-ups and re-engagement flows.',
    scalability: 'Supports audience segmentation and bulk sync for large contact lists.',
    handling: 'Email opt-ins and tags are updated through the Mailchimp Marketing API.',
  },
  {
    id: 'kit',
    name: 'Kit',
    category: 'Marketing & CRM',
    Icon: Mail,
    iconType: 'lucide',
    color: '#FB4C1F',
    howItWorks: 'Capture email subscribers and tag them based on support conversations.',
    benefit: 'Creators and coaches can nurture leads while answering questions.',
    scalability: 'Subscriber and tag updates are batched for large audiences.',
    handling: 'Kit subscriber data is synced through the ConvertKit API with opt-in respect.',
  },
  {
    id: 'intercom',
    name: 'Intercom',
    category: 'Marketing & CRM',
    Icon: SiIntercom,
    iconType: 'si',
    color: '#1F8DED',
    howItWorks: 'Import Intercom conversations and contacts when migrating to Zuri Hub.',
    benefit: 'Keep your customer history and context as you move to a more affordable stack.',
    scalability: 'Bulk migration tools handle large conversation archives.',
    handling: 'Data is imported, mapped, and de-duplicated during onboarding.',
  },

  // Website & Commerce
  {
    id: 'stripe',
    name: 'Stripe',
    category: 'Website & Commerce',
    Icon: SiStripe,
    iconType: 'si',
    color: '#635BFF',
    howItWorks: 'View Stripe payment and refund context inside customer conversations.',
    benefit: 'Resolve billing questions faster with transaction data at your fingertips.',
    scalability: 'Fetches charges, invoices, and subscriptions for any customer volume.',
    handling: 'Stripe data is read-only and refreshed on demand through secure API tokens.',
  },
  {
    id: 'paypal',
    name: 'PayPal',
    category: 'Website & Commerce',
    Icon: SiPaypal,
    iconType: 'si',
    color: '#003087',
    howItWorks: 'Pull PayPal transaction and dispute data into support tickets.',
    benefit: 'Give agents full payment context when answering refund and billing questions.',
    scalability: 'Handles merchant accounts and large transaction histories.',
    handling: 'PayPal data is accessed via OAuth and displayed alongside the customer record.',
  },
  {
    id: 'docusign',
    name: 'DocuSign',
    category: 'Website & Commerce',
    Icon: FileSignature,
    iconType: 'lucide',
    color: '#A20000',
    howItWorks: 'Trigger DocuSign envelopes and track signature status from a conversation.',
    benefit: 'Close contracts and approvals without asking customers to leave the chat.',
    scalability: 'Template-based envelopes scale from one-off deals to high-volume contracts.',
    handling: 'Envelope status and signed documents are linked back to the customer thread.',
  },
  {
    id: 'calendly',
    name: 'Calendly',
    category: 'Website & Commerce',
    Icon: SiCalendly,
    iconType: 'si',
    color: '#006BFF',
    howItWorks: 'Drop a Calendly booking link into a chat so customers can schedule instantly.',
    benefit: 'Reduce back-and-forth scheduling and book more demos and support calls.',
    scalability: 'Supports team round-robin and event-type routing.',
    handling: 'Booking events are tracked and added to the conversation timeline.',
  },
  {
    id: 'wordpress',
    name: 'WordPress',
    category: 'Website & Commerce',
    Icon: SiWordpress,
    iconType: 'si',
    color: '#21759B',
    howItWorks: 'Embed the Zuri Hub widget on WordPress sites with a simple plugin or script.',
    benefit: 'Turn any WordPress site into a lead and support channel in minutes.',
    scalability: 'The widget loads asynchronously and does not slow down your site.',
    handling: 'Visitor sessions are tracked, and chats are routed to your workspace.',
  },
];
